import { useCallback, useEffect, useState } from 'react'

import { Card, Notice, Segmented } from '@/components/ui'
import { useLocalState } from '@/hooks/useLocalState'
import { api } from '@/lib/api'
import type { CurvePoint, StatsResponse } from '@/lib/types'

/**
 * Onglet Statistiques.
 *
 * **Tout est exprimé du point de vue de la plateforme**, jamais du courtier.
 * C'est le parti pris de l'application, le même que l'onglet Positions : le
 * résultat du compte se consulte chez le courtier ; ici, on juge la thèse.
 *
 * Conséquence sur les deux extrêmes des positions ouvertes, et c'est le point
 * qui demande de l'attention : l'ordre partant inversé, **le stop du courtier
 * est ma cible** et **sa cible est mon stop**. Mon gain maximum se lit donc
 * sur ses stops, ma perte maximum sur ses cibles. Les montants sont les mêmes
 * des deux côtés ; seul le signe change.
 */
export function StatsScreen({ brokerReady }: { brokerReady: boolean }) {
  const [stats, setStats] = useState<StatsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  /*
   * Période de l'historique, retenue d'une fois sur l'autre.
   *
   * Elle était figée à trente jours. Après une semaine de changements de
   * méthode, c'est trop long pour juger ce qu'on vient de modifier ; après un
   * trimestre d'habitudes stables, c'est trop court pour juger quoi que ce
   * soit. Le serveur accepte n'importe quelle durée : seul l'écran l'imposait.
   */
  const [days, setDays] = useLocalState<'7' | '30' | '90'>('statsDays', '30')

  const load = useCallback(async () => {
    try {
      setStats(await api.stats(Number(days), 2))
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue')
    } finally {
      setLoaded(true)
    }
  }, [days])

  useEffect(() => {
    if (!brokerReady) {
      setStats(null)
      setLoaded(false)
      return
    }
    void load()
    // Les positions bougent ; l'historique, non. Dix secondes suffisent.
    const timer = window.setInterval(load, 10_000)
    return () => window.clearInterval(timer)
  }, [brokerReady, load])

  if (!brokerReady) {
    return (
      <Card title="Statistiques">
        <p className="px-3 py-6 text-center text-[13px] text-ink-muted">
          Courtier non connecté : aucune statistique à calculer.
        </p>
      </Card>
    )
  }

  if (!loaded) {
    return (
      <Card title="Statistiques">
        <p className="px-3 py-6 text-center text-[13px] text-ink-muted">Calcul en cours…</p>
      </Card>
    )
  }

  if (error) return <Notice tone="error" title="Statistiques" items={[error]} />
  if (!stats) return null

  const { currency, open, closed } = stats

  return (
    <div className="flex flex-col gap-3 xl:grid xl:grid-cols-2 xl:items-start">
      {/* ---------------------------------------------------------------- */}
      <Card title={`En cours · ${open.count} position${open.count > 1 ? 's' : ''} · stratégie`}>
        <div className="grid grid-cols-2 gap-px bg-border">
          <Figure
            label="Résultat latent"
            value={open.strategy_pnl === null ? '—' : `${signed(open.strategy_pnl)} ${currency}`}
            tone={tone(open.strategy_pnl)}
            hint="vue stratégie"
          />
          <Figure
            label="Volume total"
            value={`${open.lots} lot`}
            hint={`${open.by_symbol.length} paire${open.by_symbol.length > 1 ? 's' : ''}`}
          />
        </div>

        {/*
          Les deux extrêmes, dans MON sens. Ils répondent à la question
          qu'aucun autre écran ne posait : si tout se dénoue d'un coup, je
          gagne ou je perds combien ?
        */}
        <div className="grid grid-cols-1 gap-px border-t border-border bg-border sm:grid-cols-2">
          <Extreme
            label="Si toutes mes cibles sont atteintes"
            missing={open.without_target}
            missingText="sans cible : le gain n'a pas de plafond"
            amount={open.max_gain}
            percent={open.max_gain_percent}
            sign="+"
            tone="up"
            currency={currency}
          />
          <Extreme
            label="Si tous mes stops sont touchés"
            missing={open.without_stop}
            missingText="sans stop : la perte n'a pas de plafond"
            amount={open.max_loss}
            percent={open.max_loss_percent}
            sign="−"
            tone="down"
            currency={currency}
          />
        </div>

        {/* Une ligne, et une seule, sur le compte réel : la coupure
            automatique s'y déclenche, et l'ignorer serait dangereux. */}
        <p className="border-t border-border px-3 py-2 text-[11px] leading-relaxed text-ink-muted">
          La coupure automatique du gardien porte sur le compte réel, où ces deux
          chiffres sont inversés.
        </p>

        {open.by_symbol.length > 0 && (
          <ul className="divide-y divide-border border-t border-border">
            {open.by_symbol.map((item) => (
              <li key={item.symbol} className="flex items-baseline justify-between px-3 py-2">
                <span className="num text-[13px] text-ink">
                  {item.symbol}
                  <span className="ml-2 text-[11px] text-ink-muted">
                    {item.positions} × {item.lots} lot
                  </span>
                </span>
                <span className={`num text-[13px] font-semibold ${toneClass(item.strategy_pnl)}`}>
                  {item.strategy_pnl === null
                    ? '—'
                    : `${signed(item.strategy_pnl)} ${currency}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* ---------------------------------------------------------------- */}
      <Card
        title={`Trades clôturés · ${stats.days} jours · stratégie`}
        action={
          <div className="w-[150px]">
            <Segmented<'7' | '30' | '90'>
              dense
              value={days}
              onChange={setDays}
              options={[
                { value: '7', label: '7j' },
                { value: '30', label: '30j' },
                { value: '90', label: '90j' },
              ]}
            />
          </div>
        }
      >
        {!stats.history_supported ? (
          <p className="px-3 py-6 text-center text-[13px] text-ink-muted">
            Ce courtier ne rend pas son historique : aucun bilan possible.
          </p>
        ) : closed.count === 0 ? (
          <p className="px-3 py-6 text-center text-[13px] leading-relaxed text-ink-muted">
            Aucun trade clôturé sur la période.
            <br />
            <span className="text-[11px]">
              Les chiffres apparaîtront ici dès la première position fermée — un bilan à zéro
              n'est pas un résultat.
            </span>
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-3">
              <Figure
                label="Résultat"
                value={`${signed(closed.strategy_pnl)} ${currency}`}
                tone={tone(closed.strategy_pnl)}
                hint="vue stratégie"
              />
              <Figure
                label="Réussite"
                value={closed.win_rate === null ? '—' : `${closed.win_rate} %`}
                hint={`${closed.wins} gagnants · ${closed.losses} perdants`}
              />
              <Figure
                label="Facteur de profit"
                value={closed.profit_factor ?? '—'}
                hint={closed.profit_factor === null ? 'aucune perte' : 'gains / pertes'}
              />
              <Figure
                label="Gain moyen"
                value={closed.average_win === null ? '—' : `${signed(closed.average_win)} ${currency}`}
                tone="up"
              />
              <Figure
                label="Perte moyenne"
                value={
                  closed.average_loss === null ? '—' : `${signed(closed.average_loss)} ${currency}`
                }
                tone="down"
              />
              <Figure
                label="Repli maximal"
                value={closed.max_drawdown === null ? '—' : `−${closed.max_drawdown} ${currency}`}
                tone="down"
                hint="sur la courbe stratégie"
              />
            </div>

            <div className="border-t border-border p-3">
              <p className="mb-2 text-[11px] uppercase tracking-wide text-ink-muted">
                Courbe cumulée
              </p>
              <EquityCurve points={closed.curve} />
            </div>

            {/* L'historique, position par position, du plus récent au plus
                ancien. Les agrégats ci-dessus ne disent pas d'où vient le
                résultat ; cette liste, oui. */}
            <ul className="divide-y divide-border border-t border-border">
              {closed.trades.map((row) => (
                <li
                  key={`${row.at}-${row.symbol}-${row.strategy_result}`}
                  className="flex items-baseline justify-between gap-3 px-3 py-2"
                >
                  <span className="min-w-0 text-[13px]">
                    <span className="num font-semibold text-ink-strong">{row.symbol}</span>
                    {row.entry_side && (
                      <span
                        className={`num ml-2 text-[11px] font-semibold ${
                          row.entry_side === 'BUY' ? 'text-up' : 'text-down'
                        }`}
                      >
                        {row.entry_side === 'BUY' ? 'ACHAT' : 'VENTE'}
                      </span>
                    )}
                    {row.lots && <span className="num ml-1 text-[11px] text-ink-muted">{row.lots} lot</span>}
                    <span className="num block text-[10px] text-ink-muted">
                      {when(row.at)}
                      {row.open_price && ` · entrée ${row.open_price}`}
                      {/* Le courtier ne donne le sens que par le journal : au-delà
                          de sa fenêtre, on l'affiche comme inconnu plutôt que de
                          le deviner. */}
                      {!row.entry_side && ' · sens non retrouvé'}
                    </span>
                  </span>
                  <span
                    className={`num shrink-0 text-[14px] font-semibold ${toneClass(row.strategy_result)}`}
                  >
                    {signed(row.strategy_result)} {row.currency}
                  </span>
                </li>
              ))}
            </ul>

            {/*
              Les frais, à part et jamais inversés : swap partout, commission
              en plus sur les comptes cTrader (FTMO…). Ils sont prélevés sur le
              compte réel dans les deux sens : les ajouter au résultat de la
              stratégie transformerait un coût en profit.
            */}
            {closed.financing_count > 0 && (
              <p className="border-t border-border px-3 py-2 text-[11px] leading-relaxed text-ink-muted">
                Frais (swap, commission) :{' '}
                <span className={`num font-semibold ${toneClass(closed.financing)}`}>
                  {signed(closed.financing)} {currency}
                </span>{' '}
                sur {closed.financing_count} prélèvement
                {closed.financing_count > 1 ? 's' : ''} — comptés à part, car ils sont
                prélevés quel que soit le sens de la position.
              </p>
            )}

          </>
        )}
      </Card>

      {/* ---------------------------------------------------------------- */}
      <Card title="Journal d'activité · 48 h">
        {stats.activity.length === 0 ? (
          <p className="px-3 py-6 text-center text-[13px] text-ink-muted">
            Aucune activité enregistrée.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {stats.activity.map((entry, index) => (
              <li key={`${entry.at}-${entry.symbol}-${index}`} className="px-3 py-2">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[13px] text-ink">
                    <span className="num font-semibold text-ink-strong">{entry.symbol}</span>{' '}
                    <span className="text-ink-muted">{KINDS[entry.kind] ?? entry.raw_type}</span>
                  </span>
                  <span className="num shrink-0 text-[11px] text-ink-muted">
                    {when(entry.at)}
                  </span>
                </div>
                <p className="num mt-0.5 text-[11px] text-ink-muted">
                  {entry.entry_side && (
                    <span className={entry.entry_side === 'BUY' ? 'text-up' : 'text-down'}>
                      {entry.entry_side === 'BUY' ? 'ACHAT' : 'VENTE'}
                    </span>
                  )}
                  {entry.lots && ` ${entry.lots} lot`}
                  {entry.level && ` · ${entry.level}`}
                  {entry.status !== 'ACCEPTED' && entry.status !== 'EXECUTED' && (
                    <span className="text-warn"> · {entry.status}</span>
                  )}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  )
}

const KINDS: Record<string, string> = {
  open: 'position ouverte',
  close: 'position fermée',
  amend: 'niveaux modifiés',
  order: 'ordre exécuté',
  swap: 'swap',
  system: 'système',
}

/** Signe explicite : « 6.17 » ne dit pas si c'est un gain ou une perte. */
function signed(value: string): string {
  return Number(value) >= 0 ? `+${value}` : value
}

function tone(value: string | null): 'up' | 'down' | 'normal' {
  if (value === null) return 'normal'
  return Number(value) >= 0 ? 'up' : 'down'
}

function toneClass(value: string | null): string {
  const t = tone(value)
  return t === 'up' ? 'text-up' : t === 'down' ? 'text-down' : 'text-ink'
}

function when(at: number): string {
  return new Date(at * 1000).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Un des deux extrêmes des positions ouvertes.
 *
 * Le cas « niveau absent » prime sur le montant : additionner ce qui reste
 * quand une position n'a pas de plafond donnerait un total rassurant et faux.
 */
function Extreme({
  label,
  missing,
  missingText,
  amount,
  percent,
  sign,
  tone,
  currency,
}: {
  label: string
  missing: number
  missingText: string
  amount: string | null
  percent: string | null
  sign: '+' | '−'
  tone: 'up' | 'down'
  currency: string
}) {
  return (
    <div className="bg-panel p-3">
      <p className="mb-1 text-[11px] uppercase tracking-wide text-ink-muted">{label}</p>
      {missing > 0 ? (
        <p className="text-[13px] leading-snug text-warn">
          {missing} position{missing > 1 ? 's' : ''} {missingText}.
        </p>
      ) : amount === null ? (
        <p className="text-[13px] text-ink-muted">
          Non chiffrable (cotation de conversion manquante).
        </p>
      ) : (
        <p className={`num text-[20px] font-semibold ${tone === 'up' ? 'text-up' : 'text-down'}`}>
          {sign}
          {amount} {currency}
          {percent && (
            <span className="ml-2 text-[13px] font-normal text-ink-muted">
              {percent} % du capital
            </span>
          )}
        </p>
      )}
    </div>
  )
}

function Figure({
  label,
  value,
  hint,
  tone = 'normal',
}: {
  label: string
  value: string
  hint?: string
  tone?: 'up' | 'down' | 'normal'
}) {
  const tones = { up: 'text-up', down: 'text-down', normal: 'text-ink-strong' } as const
  return (
    <div className="bg-panel p-3">
      <p className="text-[10px] uppercase tracking-wide text-ink-muted">{label}</p>
      <p className={`num text-[18px] font-semibold ${tones[tone]}`}>{value}</p>
      {hint && <p className="text-[10px] text-ink-muted">{hint}</p>}
    </div>
  )
}

/**
 * Courbe cumulée, dessinée en SVG.
 *
 * Pas de bibliothèque : une polyligne suffit, et `lightweight-charts` est fait
 * pour des chandeliers, pas pour une courbe de quinze points.
 */
function EquityCurve({ points }: { points: CurvePoint[] }) {
  if (points.length < 2) {
    return (
      <p className="text-[12px] text-ink-muted">
        Au moins deux trades sont nécessaires pour tracer une courbe.
      </p>
    )
  }

  const values = points.map((point) => Number(point.cumulative))
  const low = Math.min(0, ...values)
  const high = Math.max(0, ...values)
  const span = high - low || 1
  const width = 300
  const height = 90

  const x = (index: number) => (index / (points.length - 1)) * width
  const y = (value: number) => height - ((value - low) / span) * height

  const path = values.map((value, index) => `${x(index)},${y(value)}`).join(' ')
  const positif = values.at(-1)! >= 0

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-24 w-full" preserveAspectRatio="none">
      {/* Ligne du zéro : sans elle, une courbe entièrement négative paraît
          monter tranquillement. */}
      <line
        x1="0"
        x2={width}
        y1={y(0)}
        y2={y(0)}
        stroke="#2a2e39"
        strokeWidth="1"
        strokeDasharray="4 4"
      />
      <polyline
        points={path}
        fill="none"
        stroke={positif ? '#26a69a' : '#ef5350'}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}
